import { Option } from '@/components/inputs/AutocompleteInput';
import useDriverOptions from './useDriverOptions';
import { AuthorizationsFilter } from '../models';
import useUserOptions from './useUserOptions';
import { useState } from 'react';
import dayjs from 'dayjs';

interface UseSearchBarProps {
  onSearch: (filters: AuthorizationsFilter) => void;
  initialFilter: AuthorizationsFilter;
  currentFilter: AuthorizationsFilter;
}

export const useSearchBar = ({
  onSearch,
  initialFilter,
  currentFilter,
}: UseSearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<AuthorizationsFilter>(currentFilter);

  const driverHook = useDriverOptions();
  const userHook = useUserOptions();

  const handleDriverSelect = (option: Option | null) => {
    driverHook.setSelectedDriver(option);
    setLocalFilter((prev) => ({
      ...prev,
      requestDriverId: (option?.data as string) || undefined,
    }));
  };

  const handleUserSelect = (option: Option | null) => {
    userHook.setSelectedUser(option);
    setLocalFilter((prev) => ({
      ...prev,
      authorizationUserId: (option?.data as string) || undefined,
    }));
  };

  const handleStatusSelect = (statusValue: string) => {
    setLocalFilter((prev) => ({
      ...prev,
      status: statusValue === '' ? undefined : (statusValue as any),
    }));
  };

  const handleRequestedFromSelect = (date: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      requestedFrom: date ? dayjs(date).startOf('day').toISOString() : undefined,
    }));
  };

  const handleRequestedToSelect = (date: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      requestedTo: date ? dayjs(date).endOf('day').toISOString() : undefined,
    }));
  };

  const resetFilter = () => {
    setLocalFilter(initialFilter);
    driverHook.resetDriver();
    userHook.resetUser();
    onSearch(initialFilter);
  };

  const handleSearch = () => {
    onSearch(localFilter);
  };

  return {
    localFilter,
    selectedDriver: driverHook.selectedDriver,
    driverSearch: driverHook.driverSearch,
    driverOptions: driverHook.driverOptions,
    isDriversLoading: driverHook.isDriversLoading,
    setDriverSearch: driverHook.setDriverSearch,
    handleDriverSelect,
    selectedUser: userHook.selectedUser,
    userSearch: userHook.userSearch,
    userOptions: userHook.userOptions,
    isUsersLoading: userHook.isUsersLoading,
    setUserSearch: userHook.setUserSearch,
    handleUserSelect,
    handleStatusSelect,
    handleRequestedFromSelect,
    handleRequestedToSelect,
    resetFilter,
    handleSearch,
  };
};

export default useSearchBar;
